(() => {
  const initializeSection = (section) => {
    if (section.dataset.k9DualReady) return;
    section.dataset.k9DualReady = 'true';

    const experience = section.querySelector('[data-k9-dual-experience]');
    const states = [...section.querySelectorAll('[data-k9-tech-state]')];
    const navigation = [...section.querySelectorAll('[data-k9-tech-nav]')];
    const staticExperience = window.matchMedia('(max-width: 989px), (prefers-reduced-motion: reduce)');
    if (!experience || states.length !== 2 || navigation.length !== 2) return;

    let activeTechnology = 'acoustic';
    let frame;
    let listening = false;
    let manualSelection = null;

    const scrollTechnology = () => {
      const bounds = experience.getBoundingClientRect();
      const scrollDistance = Math.max(experience.offsetHeight - window.innerHeight, 1);
      return -bounds.top / scrollDistance >= 0.48 ? 'tracer' : 'acoustic';
    };

    const selectorNavigation = section.querySelector('.k9-dual-tech__nav');

    // Follow the painted photo bounds without changing its frame or object-fit.
    const positionSelector = (media) => {
      const image = media.querySelector('.k9-dual-tech__image');
      let left = 0;
      let top = 0;
      let width = media.clientWidth;
      if (image && image.naturalWidth && image.naturalHeight) {
        const style = getComputedStyle(image);
        const scale = Math.min(image.clientWidth / image.naturalWidth, image.clientHeight / image.naturalHeight);
        const contained = style.objectFit === 'contain';
        width = contained ? image.naturalWidth * scale : image.clientWidth;
        const height = contained ? image.naturalHeight * scale : image.clientHeight;
        const position = style.objectPosition.split(' ').map((value) => parseFloat(value) / 100);
        left = image.offsetLeft + (image.clientWidth - width) * (position[0] ?? 0.5);
        top = image.offsetTop + (image.clientHeight - height) * (position[1] ?? 0.5);
      }
      media.style.setProperty('--k9-selector-image-left', `${left}px`);
      media.style.setProperty('--k9-selector-image-top', `${top}px`);
      media.style.setProperty('--k9-selector-image-width', `${width}px`);
    };
    const mediaObserver = new ResizeObserver((entries) => {
      entries.forEach(({ target }) => positionSelector(target.closest('.k9-dual-tech__primary-media')));
    });
    section.querySelectorAll('.k9-dual-tech__primary-media').forEach((media) => {
      mediaObserver.observe(media);
      const image = media.querySelector('.k9-dual-tech__image');
      if (image) {
        mediaObserver.observe(image);
        image.addEventListener('load', () => positionSelector(media));
      }
      positionSelector(media);
    });

    const setActiveTechnology = (technology) => {
      if (technology === activeTechnology && section.dataset.k9ActiveTechnology) return;
      activeTechnology = technology;
      section.dataset.k9ActiveTechnology = technology;
      section.classList.toggle('is-tracer', technology === 'tracer');

      states.forEach((state) => {
        const isActive = state.dataset.k9TechState === technology;
        state.classList.toggle('is-active', isActive);
        state.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        state.inert = !isActive;
      });

      navigation.forEach((link) => {
        const isActive = link.dataset.k9TechNav === technology;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('aria-pressed', String(isActive));
      });
      const activeVisual = states.find((state) => state.dataset.k9TechState === technology)
        ?.querySelector('.k9-dual-tech__primary-media');
      if (selectorNavigation && activeVisual && selectorNavigation.parentElement !== activeVisual) {
        const focusedLink = selectorNavigation.contains(document.activeElement) ? document.activeElement : null;
        activeVisual.appendChild(selectorNavigation);
        focusedLink?.focus({ preventScroll: true });
      }
    };

    // Direct controls never navigate to the scroll-story anchors.
    navigation.forEach((link) => {
      link.addEventListener('click', () => {
        manualSelection = { technology: scrollTechnology(), scrollY: window.scrollY };
        setActiveTechnology(link.dataset.k9TechNav);
      });
    });

    const updateFromScroll = () => {
      frame = undefined;
      if (staticExperience.matches) return;
      const technology = scrollTechnology();
      if (manualSelection) {
        // Resize/observer callbacks and scrolling within the same story step
        // must not undo a click. Resume only after a real boundary crossing.
        if (Math.abs(window.scrollY - manualSelection.scrollY) < 1) {
          manualSelection.technology = technology;
          return;
        }
        manualSelection.scrollY = window.scrollY;
        if (technology === manualSelection.technology) return;
        manualSelection = null;
      }
      setActiveTechnology(technology);
    };

    const requestUpdate = () => {
      if (!frame) frame = requestAnimationFrame(updateFromScroll);
    };

    const startListening = () => {
      if (listening) return;
      listening = true;
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
      requestUpdate();
    };

    const stopListening = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (frame) cancelAnimationFrame(frame);
      frame = undefined;
    };

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => entry.isIntersecting ? startListening() : stopListening(),
      { rootMargin: '20% 0px 20% 0px' }
    );

    setActiveTechnology('acoustic');
    staticExperience.addEventListener('change', () => {
      section.dataset.k9ActiveTechnology = '';
      setActiveTechnology(activeTechnology);
    });
    visibilityObserver.observe(experience);
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll('[data-k9-dual-tech]').forEach(initializeSection);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
